import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AlertCircle, Camera, Loader2, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

interface ScreenshotPreviewProps {
  projectId: string;
  deployUrl?: string;
  onImageChange: (imageUrl: string | null) => void;
  className?: string;
}

/**
 * Generate a random gradient for placeholder/default images
 */
function generateGradient(seed: string): string {
  // Simple hash function to get consistent colors
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i);
    hash = hash & hash;
  }

  const hue1 = Math.abs(hash % 360);
  const hue2 = (hue1 + 60) % 360;

  return `linear-gradient(135deg, hsl(${hue1}, 70%, 60%) 0%, hsl(${hue2}, 70%, 50%) 100%)`;
}

export function ScreenshotPreview({
  projectId,
  deployUrl,
  onImageChange,
  className,
}: ScreenshotPreviewProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFile();
  const { toast } = useToast();

  const gradient = generateGradient(projectId);

  // Notify parent when screenshot changes
  useEffect(() => {
    onImageChange(screenshotUrl);
  }, [screenshotUrl, onImageChange]);

  const generateScreenshot = useCallback(async () => {
    if (!deployUrl) {
      setError('Project must be deployed first to generate a screenshot');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Use a screenshot API service to capture the deployed URL
      // We'll use screenshotapi.net as an example (free tier available)
      const screenshotApiUrl = `https://shot.screenshotapi.net/screenshot`;
      const params = new URLSearchParams({
        url: deployUrl,
        width: '1200',
        height: '630',
        output: 'image',
        file_type: 'png',
        wait_for_event: 'load',
      });

      const response = await fetch(`${screenshotApiUrl}?${params}`);

      if (!response.ok) {
        throw new Error('Failed to generate screenshot');
      }

      const blob = await response.blob();
      const file = new File([blob], `${projectId}-screenshot.png`, { type: 'image/png' });

      // Upload to Blossom
      const [[_, url]] = await uploadFile(file);

      setScreenshotUrl(url);

      toast({
        title: 'Screenshot generated!',
        description: 'Your project screenshot has been created',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate screenshot';
      setError(errorMessage);
      toast({
        title: 'Screenshot generation failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  }, [deployUrl, projectId, uploadFile, toast]);

  const removeScreenshot = () => {
    setScreenshotUrl(null);
    setError(null);
  };

  const isLoading = isGenerating || isUploading;

  return (
    <div className={cn('space-y-3', className)}>
      <Label className="text-sm font-medium">Preview Image</Label>

      {/* Preview card */}
      <div className="relative rounded-lg overflow-hidden border border-border aspect-[1200/630] bg-muted">
        {screenshotUrl ? (
          <img
            src={screenshotUrl}
            alt="Project screenshot"
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: gradient }}
          >
            <div className="text-white/90 text-sm font-medium px-4 py-2 bg-black/20 rounded-lg backdrop-blur-sm">
              {projectId}
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
            <div className="text-center space-y-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
              <p className="text-sm text-muted-foreground">
                {isGenerating ? 'Generating screenshot...' : 'Uploading...'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {screenshotUrl ? (
          <Button
            variant="outline"
            size="sm"
            onClick={removeScreenshot}
            disabled={isLoading}
            className="gap-2"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Remove Screenshot
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={generateScreenshot}
            disabled={isLoading || !deployUrl}
            className="gap-2"
          >
            <Camera className="h-3.5 w-3.5" />
            Generate Screenshot
          </Button>
        )}
      </div>

      {/* Helper text */}
      <p className="text-xs text-muted-foreground">
        {screenshotUrl
          ? 'This screenshot will be included in your repository announcement'
          : deployUrl
            ? 'Click to generate a screenshot of your deployed project'
            : 'Deploy your project first to enable screenshot generation'}
      </p>

      {/* Error display */}
      {error && (
        <Alert variant="destructive" className="bg-destructive/10">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
